import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getStatusChipSx } from '../../constants/statusColors';
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { csAdminApi } from '../../services/api';
import { mapCompanyFromApi, formatDisplayDate, parseCompanyDescription, mapShareholderFromApi } from '../../utils/apiMappers';

// Import tabs
import OverviewTab from './tabs/OverviewTab';
import DocumentsTab from './tabs/DocumentsTab';
import DirectorsTab from './tabs/DirectorsTab';
import CommitteesTab from './tabs/CommitteesTab';
import SubsidiariesTab from './tabs/SubsidiariesTab';
import ParentCompanyTab from './tabs/ParentCompanyTab';
import ShareholdersTab from './tabs/ShareholdersTab';
import OfficersTab from './tabs/OfficersTab';
import GovernanceStructureTab from './tabs/GovernanceStructureTab';
import SettingsTab from './tabs/SettingsTab';
import AuditLogTab from './tabs/AuditLogTab';

const CompanyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [company, setCompany] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [counts, setCounts] = useState({
    documents: 0,
    directors: 0,
    committees: 0,
    subsidiaries: 0,
    parentCompanies: 0,
    shareholders: 0,
    officers: 0,
  });

  useEffect(() => {
    fetchCompanyDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDocumentsCountChange = useCallback((count) => {
    setCounts((c) => (c.documents === count ? c : { ...c, documents: count }));
  }, []);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);

      const [companyRes, shareholdersRes, officersRes, committeesRes, auditRes, companiesRes] = await Promise.all([
        csAdminApi.getCompanyById(id),
        csAdminApi.getCompanyShareholders(id).catch(() => ({ data: { data: [] } })),
        csAdminApi.getCompanyOfficers(id).catch(() => ({ data: { data: [] } })),
        csAdminApi.getCommittees({ company_id: id }).catch(() => ({ data: { data: [] } })),
        csAdminApi.getCompanyAuditLog(id).catch(() => ({ data: { data: [] } })),
        csAdminApi.getCompanies().catch(() => ({ data: { data: [] } })),
      ]);
      const mapped = mapCompanyFromApi(companyRes.data?.data);
      if (!mapped) {
        setError('Company not found');
        setLoading(false);
        return;
      }
      const shareholders = (shareholdersRes.data?.data || []).map(mapShareholderFromApi).filter(Boolean);
      const officers = officersRes.data?.data || [];
      const committees = committeesRes.data?.data || [];
      const auditLogs = auditRes.data?.data || [];
      const allCompanies = companiesRes.data?.data || [];
      const subsidiaries = allCompanies.filter(
        (companyRow) => String(companyRow.parent_company_id ?? companyRow.parentCompanyId) === String(id)
      );
      const contact = parseCompanyDescription(mapped.description);
      const fullCompanyData = {
        ...mapped,
        ...contact,
        name: mapped.name,
        tradeLicenseNumber: mapped.tradeLicenseNumber || '',
        shareholders,
        officers,
        subsidiaries,
        auditLogs,
      };
      setCompany(fullCompanyData);
      setCounts({
        documents: mapped.document_count ?? 0,
        directors: mapped.director_count ?? 0,
        committees: committees.length,
        subsidiaries: subsidiaries.length,
        parentCompanies: mapped.parent_company_id ? 1 : 0,
        shareholders: shareholders.length,
        officers: officers.filter((o) => (o.status || '').toUpperCase() === 'ACTIVE').length,
      });
      setError('');
    } catch (err) {
      console.error('Error fetching company details:', err);
      setError('Failed to load company details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !company) {
    return (
      <Box sx={{ px: 3, py: 3 }}>
        <Alert severity="error">{error || 'Company not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/companies')} sx={{ mt: 2 }}>
          Back to Companies
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/companies')} sx={{ mb: 2 }}>
          Back to Companies
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h4" component="h1">
                {company.name}
              </Typography>
              <Chip
                label={company.status}
                sx={{
                  ...getStatusChipSx(company.status),
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 24,
                  borderRadius: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
                size="small"
              />
              <Chip
                label={company.type}
                sx={{
                  bgcolor: 'transparent',
                  color: '#D32F2F',
                  border: '1px solid #D32F2F',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: 24,
                  borderRadius: '12px',
                }}
                size="small"
              />
            </Box>
            <Typography variant="body2" color="textSecondary">
              {company.tradeLicenseNumber} • Created: {formatDisplayDate(company.createdDate)}
            </Typography>
          </Box>

          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/companies/${id}/edit`)}>
            Edit Company
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab label="OVERVIEW" />
          <Tab label={`DOCUMENTS (${counts.documents})`} />
          <Tab label={`DIRECTORS (${counts.directors})`} />
          <Tab label={`COMMITTEES (${counts.committees})`} />
          <Tab label={`SHAREHOLDERS (${counts.shareholders})`} />
          <Tab label={`OFFICERS (${counts.officers})`} />
          <Tab label="GOVERNANCE" />
          {counts.subsidiaries > 0 && <Tab label={`SUBSIDIARIES (${counts.subsidiaries})`} />}
          {counts.parentCompanies > 0 && <Tab label={`PARENT COMPANY (${counts.parentCompanies})`} />}
          <Tab label="SETTINGS" />
          <Tab label="AUDIT LOG" />
        </Tabs>
      </Box>

      {/* Tab Panels - dynamic indexing based on which tabs are shown */}
      {(() => {
        let tabIndex = 0;
        const tabs = [];
        
        // Fixed tabs (0-6)
        tabs.push({ index: tabIndex++, component: <OverviewTab company={company} counts={counts} /> });
        tabs.push({ index: tabIndex++, component: <DocumentsTab companyId={id} company={company} onDocumentsChange={handleDocumentsCountChange} /> });
        tabs.push({ index: tabIndex++, component: <DirectorsTab companyId={id} companyName={company?.name} /> });
        tabs.push({ index: tabIndex++, component: <CommitteesTab companyId={id} /> });
        tabs.push({ index: tabIndex++, component: <ShareholdersTab companyId={id} companyName={company.name} shareholders={company.shareholders} onRefresh={fetchCompanyDetails} /> });
        tabs.push({ index: tabIndex++, component: <OfficersTab companyId={id} companyName={company.name} officers={company.officers} onRefresh={fetchCompanyDetails} /> });
        tabs.push({ index: tabIndex++, component: <GovernanceStructureTab companyId={id} company={company} /> });
        
        // Optional Subsidiaries tab (if company has subsidiaries)
        if (counts.subsidiaries > 0) {
          tabs.push({ index: tabIndex++, component: <SubsidiariesTab companyId={id} company={company} /> });
        }
        
        // Optional Parent Company tab (if company has a parent)
        if (counts.parentCompanies > 0) {
          tabs.push({ index: tabIndex++, component: <ParentCompanyTab companyId={id} company={company} /> });
        }
        
        // Settings tab
        tabs.push({ index: tabIndex++, component: <SettingsTab companyId={id} company={company} /> });
        
        // Audit Log (always last)
        tabs.push({ index: tabIndex++, component: <AuditLogTab companyId={id} auditLogs={company.auditLogs || []} /> });
        
        // Render the active tab
        const activeTabConfig = tabs.find(t => t.index === activeTab);
        return activeTabConfig ? activeTabConfig.component : null;
      })()}
    </Box>
  );
};

export default CompanyDetails;
